import { DailyTotals } from '@/types'

export type DailyMetricKey =
  | 'total_clients_count'
  | 'active_subscribers_count'
  | 'active_visitors_count'
  | 'active_visitors_28d_count'
  | 'base_visitors_21d_count'
  | 'base_visitors_28d_count'

export interface SnapshotBaseline {
  total_clients_count?: number | null
  active_subscribers_count?: number | null
  active_visitors_count?: number | null
  active_visitors_28d_count?: number | null
  base_visitors_21d_count?: number | null
  base_visitors_28d_count?: number | null
}

type SnapshotRow = Pick<
  DailyTotals,
  | 'studio_internal_id'
  | 'metric_date'
  | 'total_clients_count'
  | 'active_subscribers_count'
  | 'active_visitors_count'
  | 'active_visitors_28d_count'
  | 'base_visitors_21d_count'
  | 'base_visitors_28d_count'
>

function isDefinedNumber(value: number | null | undefined): value is number {
  return value !== null && value !== undefined
}

export function getLatestSnapshotBaseline(rows: SnapshotRow[]): SnapshotBaseline {
  const sorted = [...rows].sort((a, b) => a.metric_date.localeCompare(b.metric_date))
  let totalClientsCount: number | null | undefined = undefined
  let activeSubscribersCount: number | null | undefined = undefined
  let activeVisitorsCount: number | null | undefined = undefined
  let activeVisitors28Count: number | null | undefined = undefined
  let baseVisitors21Count: number | null | undefined = undefined
  let baseVisitors28Count: number | null | undefined = undefined

  for (const row of sorted) {
    if (isDefinedNumber(row.total_clients_count)) {
      totalClientsCount = row.total_clients_count
    }
    if (isDefinedNumber(row.active_subscribers_count)) {
      activeSubscribersCount = row.active_subscribers_count
    }
    if (isDefinedNumber(row.active_visitors_count)) {
      activeVisitorsCount = row.active_visitors_count
    }
    if (isDefinedNumber(row.active_visitors_28d_count)) {
      activeVisitors28Count = row.active_visitors_28d_count
    }
    if (isDefinedNumber(row.base_visitors_21d_count)) {
      baseVisitors21Count = row.base_visitors_21d_count
    }
    if (isDefinedNumber(row.base_visitors_28d_count)) {
      baseVisitors28Count = row.base_visitors_28d_count
    }
  }

  return {
    total_clients_count: totalClientsCount,
    active_subscribers_count: activeSubscribersCount,
    active_visitors_count: activeVisitorsCount,
    active_visitors_28d_count: activeVisitors28Count,
    base_visitors_21d_count: baseVisitors21Count,
    base_visitors_28d_count: baseVisitors28Count
  }
}

export function fillSnapshotMetrics<T extends SnapshotRow>(
  rows: T[],
  baseline?: SnapshotBaseline
): T[] {
  const sorted = [...rows].sort((a, b) => a.metric_date.localeCompare(b.metric_date))
  let totalClientsCount = baseline?.total_clients_count
  let activeSubscribersCount = baseline?.active_subscribers_count
  let activeVisitorsCount = baseline?.active_visitors_count
  let activeVisitors28Count = baseline?.active_visitors_28d_count
  let baseVisitors21Count = baseline?.base_visitors_21d_count
  let baseVisitors28Count = baseline?.base_visitors_28d_count

  return sorted.map((row) => {
    if (isDefinedNumber(row.total_clients_count)) {
      totalClientsCount = row.total_clients_count
    }
    if (isDefinedNumber(row.active_subscribers_count)) {
      activeSubscribersCount = row.active_subscribers_count
    }
    if (isDefinedNumber(row.active_visitors_count)) {
      activeVisitorsCount = row.active_visitors_count
    }
    if (isDefinedNumber(row.active_visitors_28d_count)) {
      activeVisitors28Count = row.active_visitors_28d_count
    }
    if (isDefinedNumber(row.base_visitors_21d_count)) {
      baseVisitors21Count = row.base_visitors_21d_count
    }
    if (isDefinedNumber(row.base_visitors_28d_count)) {
      baseVisitors28Count = row.base_visitors_28d_count
    }

    return {
      ...row,
      total_clients_count: isDefinedNumber(row.total_clients_count)
        ? row.total_clients_count
        : (isDefinedNumber(totalClientsCount) ? totalClientsCount : row.total_clients_count),
      active_subscribers_count: isDefinedNumber(row.active_subscribers_count)
        ? row.active_subscribers_count
        : (isDefinedNumber(activeSubscribersCount) ? activeSubscribersCount : row.active_subscribers_count),
      active_visitors_count: isDefinedNumber(row.active_visitors_count)
        ? row.active_visitors_count
        : (isDefinedNumber(activeVisitorsCount) ? activeVisitorsCount : row.active_visitors_count),
      active_visitors_28d_count: isDefinedNumber(row.active_visitors_28d_count)
        ? row.active_visitors_28d_count
        : (isDefinedNumber(activeVisitors28Count) ? activeVisitors28Count : row.active_visitors_28d_count),
      base_visitors_21d_count: isDefinedNumber(row.base_visitors_21d_count)
        ? row.base_visitors_21d_count
        : (isDefinedNumber(baseVisitors21Count) ? baseVisitors21Count : row.base_visitors_21d_count),
      base_visitors_28d_count: isDefinedNumber(row.base_visitors_28d_count)
        ? row.base_visitors_28d_count
        : (isDefinedNumber(baseVisitors28Count) ? baseVisitors28Count : row.base_visitors_28d_count)
    }
  })
}

export function buildSnapshotBaselinesByStudio(rows: SnapshotRow[]): Map<number, SnapshotBaseline> {
  const grouped = new Map<number, SnapshotRow[]>()

  for (const row of rows) {
    const existing = grouped.get(row.studio_internal_id) || []
    existing.push(row)
    grouped.set(row.studio_internal_id, existing)
  }

  const baselines = new Map<number, SnapshotBaseline>()
  for (const [studioId, studioRows] of grouped.entries()) {
    baselines.set(studioId, getLatestSnapshotBaseline(studioRows))
  }

  return baselines
}

export function aggregateSnapshotMetricsByDate(
  rows: SnapshotRow[],
  options?: {
    studioIds?: number[]
    baselinesByStudio?: Map<number, SnapshotBaseline>
  }
): Map<string, SnapshotBaseline> {
  const rowStudioIds = new Set(rows.map((row) => row.studio_internal_id))
  const baselineStudioIds = options?.baselinesByStudio
    ? Array.from(options.baselinesByStudio.keys())
    : []
  const studioIds = options?.studioIds || Array.from(new Set([...rowStudioIds, ...baselineStudioIds]))
  const grouped = new Map<number, SnapshotRow[]>()
  const dates = Array.from(new Set(rows.map((row) => row.metric_date))).sort((a, b) => a.localeCompare(b))

  for (const studioId of studioIds) {
    grouped.set(
      studioId,
      rows
        .filter((row) => row.studio_internal_id === studioId)
        .sort((a, b) => a.metric_date.localeCompare(b.metric_date))
    )
  }

  const rowIndexes = new Map<number, number>(studioIds.map((studioId) => [studioId, 0]))
  const currentState = new Map<number, SnapshotBaseline>()
  for (const studioId of studioIds) {
    currentState.set(studioId, options?.baselinesByStudio?.get(studioId) || {})
  }

  const aggregated = new Map<string, SnapshotBaseline>()

  for (const date of dates) {
    for (const studioId of studioIds) {
      const studioRows = grouped.get(studioId) || []
      let currentIndex = rowIndexes.get(studioId) || 0
      const state = currentState.get(studioId) || {}

      while (currentIndex < studioRows.length && studioRows[currentIndex].metric_date <= date) {
        const row = studioRows[currentIndex]
        if (isDefinedNumber(row.total_clients_count)) {
          state.total_clients_count = row.total_clients_count
        }
        if (isDefinedNumber(row.active_subscribers_count)) {
          state.active_subscribers_count = row.active_subscribers_count
        }
        if (isDefinedNumber(row.active_visitors_count)) {
          state.active_visitors_count = row.active_visitors_count
        }
        if (isDefinedNumber(row.active_visitors_28d_count)) {
          state.active_visitors_28d_count = row.active_visitors_28d_count
        }
        if (isDefinedNumber(row.base_visitors_21d_count)) {
          state.base_visitors_21d_count = row.base_visitors_21d_count
        }
        if (isDefinedNumber(row.base_visitors_28d_count)) {
          state.base_visitors_28d_count = row.base_visitors_28d_count
        }
        currentIndex += 1
      }

      rowIndexes.set(studioId, currentIndex)
      currentState.set(studioId, state)
    }

    let totalClientsSum = 0
    let totalClientsDefined = false
    let activeSubscribersSum = 0
    let activeSubscribersDefined = false
    let activeVisitorsSum = 0
    let activeVisitorsDefined = false
    let activeVisitors28Sum = 0
    let activeVisitors28Defined = false
    let baseVisitors21Sum = 0
    let baseVisitors21Defined = false
    let baseVisitors28Sum = 0
    let baseVisitors28Defined = false

    for (const studioId of studioIds) {
      const state = currentState.get(studioId)
      if (isDefinedNumber(state?.total_clients_count)) {
        totalClientsSum += state.total_clients_count
        totalClientsDefined = true
      }
      if (isDefinedNumber(state?.active_subscribers_count)) {
        activeSubscribersSum += state.active_subscribers_count
        activeSubscribersDefined = true
      }
      if (isDefinedNumber(state?.active_visitors_count)) {
        activeVisitorsSum += state.active_visitors_count
        activeVisitorsDefined = true
      }
      if (isDefinedNumber(state?.active_visitors_28d_count)) {
        activeVisitors28Sum += state.active_visitors_28d_count
        activeVisitors28Defined = true
      }
      if (isDefinedNumber(state?.base_visitors_21d_count)) {
        baseVisitors21Sum += state.base_visitors_21d_count
        baseVisitors21Defined = true
      }
      if (isDefinedNumber(state?.base_visitors_28d_count)) {
        baseVisitors28Sum += state.base_visitors_28d_count
        baseVisitors28Defined = true
      }
    }

    aggregated.set(date, {
      total_clients_count: totalClientsDefined ? totalClientsSum : null,
      active_subscribers_count: activeSubscribersDefined ? activeSubscribersSum : null,
      active_visitors_count: activeVisitorsDefined ? activeVisitorsSum : null,
      active_visitors_28d_count: activeVisitors28Defined ? activeVisitors28Sum : null,
      base_visitors_21d_count: baseVisitors21Defined ? baseVisitors21Sum : null,
      base_visitors_28d_count: baseVisitors28Defined ? baseVisitors28Sum : null
    })
  }

  return aggregated
}

export function getLatestDailyMetricValue(
  data: DailyTotals[],
  key: DailyMetricKey
): number | undefined {
  const sorted = data
    .filter((day) => isDefinedNumber(day[key]))
    .sort((a, b) => a.metric_date.localeCompare(b.metric_date))

  const value = sorted.length > 0 ? sorted[sorted.length - 1][key] : undefined
  return isDefinedNumber(value) ? value : undefined
}

export function getLatestDailyMetricValueBeforeDate(
  data: DailyTotals[],
  key: DailyMetricKey,
  beforeDate: string
): number | undefined {
  const sorted = data
    .filter((day) => day.metric_date < beforeDate && isDefinedNumber(day[key]))
    .sort((a, b) => a.metric_date.localeCompare(b.metric_date))

  const value = sorted.length > 0 ? sorted[sorted.length - 1][key] : undefined
  return isDefinedNumber(value) ? value : undefined
}

export function getLatestDailyMetricValueOnOrBeforeDate(
  data: DailyTotals[],
  key: DailyMetricKey,
  onOrBeforeDate: string
): number | undefined {
  const sorted = data
    .filter((day) => day.metric_date <= onOrBeforeDate && isDefinedNumber(day[key]))
    .sort((a, b) => a.metric_date.localeCompare(b.metric_date))

  const value = sorted.length > 0 ? sorted[sorted.length - 1][key] : undefined
  return isDefinedNumber(value) ? value : undefined
}

export function getFirstDailyMetricValueOnOrAfterDate(
  data: DailyTotals[],
  key: DailyMetricKey,
  onOrAfterDate: string,
  onOrBeforeDate?: string
): number | undefined {
  const sorted = data
    .filter((day) => {
      if (day.metric_date < onOrAfterDate) return false
      if (onOrBeforeDate && day.metric_date > onOrBeforeDate) return false
      return isDefinedNumber(day[key])
    })
    .sort((a, b) => a.metric_date.localeCompare(b.metric_date))

  const value = sorted.length > 0 ? sorted[0][key] : undefined
  return isDefinedNumber(value) ? value : undefined
}

export function getDailyMetricValueOnDate(
  data: DailyTotals[],
  key: DailyMetricKey,
  date: string
): number | undefined {
  const row = data.find((day) => day.metric_date === date)
  const value = row?.[key]
  return isDefinedNumber(value) ? value : undefined
}

interface ResolveRangeMetricValueParams {
  dataInRange: DailyTotals[]
  dataBeforeRange: DailyTotals[]
  key: DailyMetricKey
  rangeStart?: string
  rangeEnd?: string
  todayIso?: string
  fallbackCurrentValue?: number
  fallbackGrowth?: number
  allowGrowthFallback?: boolean
}

export function resolveRangeStartMetricValue({
  dataInRange,
  dataBeforeRange,
  key,
  rangeStart,
  rangeEnd,
  fallbackCurrentValue,
  fallbackGrowth,
  allowGrowthFallback = false
}: ResolveRangeMetricValueParams): number | undefined {
  if (!rangeStart) return undefined

  const firstInRange = getFirstDailyMetricValueOnOrAfterDate(
    dataInRange,
    key,
    rangeStart,
    rangeEnd
  )
  if (firstInRange !== undefined) return firstInRange

  const previousValue = getLatestDailyMetricValueBeforeDate(dataBeforeRange, key, rangeStart)
  if (previousValue !== undefined) return previousValue

  if (allowGrowthFallback && fallbackCurrentValue !== undefined && fallbackGrowth !== undefined) {
    return fallbackCurrentValue - fallbackGrowth
  }

  return undefined
}

export function resolveRangeGrowthStartMetricValue({
  dataInRange,
  dataBeforeRange,
  key,
  rangeStart,
  rangeEnd,
  fallbackCurrentValue,
  fallbackGrowth,
  allowGrowthFallback = false
}: ResolveRangeMetricValueParams): number | undefined {
  if (!rangeStart) return undefined

  const previousValue = getLatestDailyMetricValueBeforeDate(dataBeforeRange, key, rangeStart)
  if (previousValue !== undefined) return previousValue

  if (allowGrowthFallback && fallbackCurrentValue !== undefined && fallbackGrowth !== undefined) {
    return fallbackCurrentValue - fallbackGrowth
  }

  return getFirstDailyMetricValueOnOrAfterDate(
    dataInRange,
    key,
    rangeStart,
    rangeEnd
  )
}

export function resolveRangeEndMetricValue({
  dataInRange,
  dataBeforeRange,
  key,
  rangeStart,
  rangeEnd,
  todayIso
}: ResolveRangeMetricValueParams): number | undefined {
  if (!rangeStart || !rangeEnd) {
    return getLatestDailyMetricValue(dataInRange, key)
  }

  if (todayIso && rangeStart > todayIso) {
    return undefined
  }

  const effectiveEnd = todayIso && rangeEnd > todayIso ? todayIso : rangeEnd
  const latestUpToEnd = getLatestDailyMetricValueOnOrBeforeDate(
    dataInRange,
    key,
    effectiveEnd
  )
  if (latestUpToEnd !== undefined) return latestUpToEnd

  const rangeIncludesToday = todayIso ? rangeStart <= todayIso && todayIso <= rangeEnd : false
  if (!rangeIncludesToday) return undefined

  return getLatestDailyMetricValueBeforeDate(dataBeforeRange, key, rangeStart)
}

interface CalculateClientRetentionParams {
  clientsAtStart?: number
  clientsAtEnd: number
  newClientsCount: number
  upgradesCount?: number
  returningClientsCount: number
}

export function calculateClientRetention({
  clientsAtStart,
  clientsAtEnd,
  newClientsCount,
  upgradesCount = 0,
  returningClientsCount
}: CalculateClientRetentionParams): number | undefined {
  if (!clientsAtStart || clientsAtStart <= 0) return undefined

  return ((clientsAtEnd - newClientsCount - upgradesCount - returningClientsCount) / clientsAtStart) * 100
}
